// frontend/src/components/SystemAdministration.js
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Database, Users, Activity, BarChart3, Shield, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { useScrollableTabs } from '../hooks/useScrollableTabs';
import { isSystemAdmin } from '../utils/roleUtils';
import PreferenceDefinitions from './admin/PreferenceDefinitions';
import BulkOperations from './admin/BulkOperations';
import SystemAudit from './admin/SystemAudit';
import PreferenceAnalytics from './admin/PreferenceAnalytics';
import SystemSmsPolicy from './admin/SystemSmsPolicy';

/**
 * SystemAdministration Component
 * Main dashboard for super admin and above system management features
 */
const SystemAdministration = ({ userRole }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('definitions');
  const { tabsRef, canScrollLeft, canScrollRight, checkTabsScroll, scrollTabs } = useScrollableTabs();

  // Check if user has system admin privileges
  if (!isSystemAdmin(userRole)) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('accessDenied')}
          </h3>
          <p className="text-gray-600">
            {t('systemAdminAccessRequired')}
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'definitions',
      name: t('preferenceDefinitions'),
      icon: Database,
      component: PreferenceDefinitions,
      description: t('manageSystemPreferenceDefinitions')
    },
    {
      id: 'bulk',
      name: t('bulkOperations'),
      icon: Users,
      component: BulkOperations,
      description: t('performBulkPreferenceOperations')
    },
    {
      id: 'audit',
      name: t('systemAudit'),
      icon: Activity,
      component: SystemAudit,
      description: t('viewPreferenceAuditLogs')
    },
    {
      id: 'analytics',
      name: t('analytics'),
      icon: BarChart3,
      component: PreferenceAnalytics,
      description: t('analyzePreferenceUsageAndTrends')
    },
    {
      id: 'sms-policy',
      name: t('systemSmsPolicy'),
      icon: MessageSquare,
      component: SystemSmsPolicy,
      description: t('systemSmsPolicyDescription')
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || PreferenceDefinitions;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          <Settings className="inline w-6 h-6 mr-2" />
          {t('systemAdministration')}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('manageSystemWidePreferencesAndConfiguration')}
        </p>
      </div>

      {/* Role Badge */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center">
          <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('systemAdministratorAccess')}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-300">
              {t('currentRole')}: {userRole.replace('_', ' ').toUpperCase()}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 relative">
        {/* Left scroll indicator */}
        {canScrollLeft && (
          <button
            onClick={() => scrollTabs('left')}
            className="absolute left-0 top-0 bottom-0 z-10 px-1 flex items-center bg-gradient-to-r from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
            aria-label="Scroll tabs left"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        )}

        {/* Right scroll indicator */}
        {canScrollRight && (
          <button
            onClick={() => scrollTabs('right')}
            className="absolute right-0 top-0 bottom-0 z-10 px-1 flex items-center bg-gradient-to-l from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
            aria-label="Scroll tabs right"
          >
            <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        )}

        <nav
          ref={tabsRef}
          onScroll={checkTabsScroll}
          className="-mb-px flex space-x-8 overflow-x-auto scrollbar-thin"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 inline mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Description */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {tabs.find(tab => tab.id === activeTab)?.description}
        </p>
      </div>

      {/* Active Tab Content */}
      <div className="bg-white dark:bg-gray-900 shadow rounded-lg">
        <ActiveComponent userRole={userRole} />
      </div>
    </div>
  );
};

export default SystemAdministration;
